<template>
  <div class="metrics-assignment-summary">
    <div class="metrics-assignment-summary__header">
      <div class="metrics-assignment-summary__header-left">
        <span class="metrics-assignment-summary__title heading-s">
          {{ $t("metrics.classByAssignment") }}
        </span>
        <!-- <eg-icon
          icon="information"
          class="metrics-assignment-summary__info-icon"
        /> -->
      </div>
      <eg-dropdown
        ref="dropdown"
        :text="selectedSortLabel"
        class="metrics-assignment-summary__sort"
      >
        <eg-b-dropdown-item-button
          v-for="option in sortOptions"
          :key="option.value"
          @click="selectedSort = option.value"
        >
          {{ option.label }}
        </eg-b-dropdown-item-button>
      </eg-dropdown>
    </div>

    <p class="metrics-assignment-summary__subtitle paragraph-s">
      {{ $t("metrics.avgScorePerAssignment") }}
    </p>

    <div class="metrics-assignment-summary__body">
      <infinity-loading v-if="isLoading && !hasData" />
      <div v-else-if="hasData" class="metrics-assignment-summary__assignments">
        <router-link
          v-for="assignment in sortedAssignments"
          :key="assignment.id"
          :to="getAssignmentPath(assignment)"
          class="metrics-assignment-summary__item"
        >
          <div class="metrics-assignment-summary__item-info">
            <span class="metrics-assignment-summary__item-name">{{
              assignment.name
            }}</span>
            <span class="metrics-assignment-summary__item-date caption-s">{{
              formatDate(assignment.date)
            }}</span>
          </div>
          <div class="metrics-assignment-summary__item-right">
            <span
              v-if="assignment.avgScore != null"
              class="metrics-assignment-summary__item-score"
              >{{ assignment.avgScore }}%</span
            >
            <span
              v-else
              class="metrics-assignment-summary__item-no-data caption-s"
              >{{ $t("metrics.noData") }}</span
            >
            <eg-icon
              icon="chevron-right"
              class="metrics-assignment-summary__item-chevron"
            />
          </div>
        </router-link>
      </div>
      <metrics-empty-state v-else />
    </div>
  </div>
</template>

<script>
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";

import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";

export default {
  name: "MetricsAssignmentSummary",

  components: {
    MetricsEmptyState,
    InfinityLoading,
  },

  mixins: [dropdownHideOnLeaveMixin],

  props: {
    assignments: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    assignmentBasePath: {
      type: String,
      default: null,
    },
    assignmentClassMap: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      selectedSort: "recent",
    };
  },

  computed: {
    sortOptions() {
      return [
        { value: "recent", label: this.$t("metrics.mostRecent") },
        { value: "score_desc", label: this.$t("metrics.highestScore") },
        { value: "score_asc", label: this.$t("metrics.lowestScore") },
      ];
    },

    hasData() {
      return this.assignments && this.assignments.length > 0;
    },

    selectedSortLabel() {
      const found = this.sortOptions.find((o) => o.value === this.selectedSort);
      return found ? found.label : "";
    },

    sortedAssignments() {
      const arr = [...this.assignments];
      if (this.selectedSort === "score_desc")
        return arr.sort((a, b) => b.avgScore - a.avgScore);
      if (this.selectedSort === "score_asc")
        return arr.sort((a, b) => a.avgScore - b.avgScore);
      return arr; // "recent" — keep original order from API
    },
  },

  methods: {
    getAssignmentPath(assignment) {
      const classId = this.assignmentClassMap[assignment.id];
      if (classId) {
        return `${this.assignmentBasePath}/class/${classId}/assignment/${assignment.id}`;
      }
      return `${this.assignmentBasePath}/assignment/${assignment.id}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_card";

.metrics-assignment-summary {
  @include metrics-card;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 460px;
  max-width: 30%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__title {
    font-size: var(--typography-font-size-fs-600);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white);
  }

  &__info-icon {
    color: var(--color-icon-neutral-secondary, #8a97a4);
    font-size: 14px;
    cursor: help;
  }

  &__subtitle {
    color: var(--color-text-neutral-fade);
    margin-bottom: 16px;
  }

  &__sort {
    @include metrics-dropdown;
    ::v-deep .btn {
      padding: 4px 10px;
    }
  }

  &__body {
    position: relative;
    overflow-y: auto;
    max-height: 360px;
  }
  &__assignments {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    text-decoration: none;
    border-radius: var(--radius-rd-300, 8px);
    border: 1px solid var(--color-stroke-neutral-default);

    &:hover {
      background: var(--color-surface-neutral-container-1, #f7f9fa);
      border-radius: 8px;
    }
  }

  &__item-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  &__item-name {
    color: var(--color-text-neutral-on-white);
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-600, 600);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__item-date {
    color: var(--color-text-neutral-fade);
  }

  &__item-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__item-score {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 10px;
    border-radius: 100px;
    background: var(--colors-green-100, #d9f2e0);
    color: var(--color-text-semantic-onSuccess, #308c4a);
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-700, 700);
    min-width: 44px;
  }

  &__item-no-data {
    color: var(--color-text-neutral-fade);
    min-width: 44px;
    text-align: center;
    line-height: 18px;
    height: 16px;
  }

  &__item-chevron.eg-icon {
    font-size: 18px;
    color: var(--color-text-neutral-fade) !important;
    font-weight: var(--typography-font-weight-fw-600);
  }
}
</style>
